import { getComponentBuildAttemptPaths, getComponentRunPaths } from '@secureblob/sdk/componentJobs'
import {
    getRecentBrowserHttpErrors,
    type BrowserHttpErrorEntry,
} from '@secureblob/sdk/browserHttpErrors'
import type { EngineManager, SyncState } from '../../engine/types'
import {
    buildRuntimeDebugSnapshot,
    type RuntimeDebugSnapshot,
} from '../../testing/runtimeDebugSnapshot'
import {
    formatConsoleError,
    type BrowserNetworkFailureEntry,
} from '../../testing/browserArtifactSignals'
import {
    getRuntimeEventTrace,
    type RuntimeEventEntry,
} from '../../testing/runtimeEventTrace'

const COMPONENT_JOB_PROBE_CONSOLE_STORAGE_KEY = 'sb:component-job-probe-console-errors'
const COMPONENT_JOB_PROBE_NETWORK_STORAGE_KEY = 'sb:component-job-probe-network-failures'
const MAX_COMPONENT_JOB_PROBE_CONSOLE_ERRORS = 20
const MAX_COMPONENT_JOB_PROBE_NETWORK_FAILURES = 20

let consoleCaptureInstalled = false
let originalConsoleError: typeof console.error | null = null
let runtimeConsoleErrors: string[] = []
let runtimeNetworkFailures: BrowserNetworkFailureEntry[] = []

export interface ComponentJobRunProbeCaptureMarker {
    consoleErrorCount: number
    networkFailureCount: number
    httpErrorCount: number
    runtimeTraceSinceId: number | null
}

/** §258: SQLite lane state snapshot for the active workspace. */
export interface SqliteLaneState {
    lane: string
    pageCount: number
    dirtyPageCount: number
    transactionMode: string
    lastCommitAt: string | null
}

export interface ComponentJobRunProbe {
    schema: 'secureblob.component-job-run-probe/v1'
    capturedAt: string
    sourcePath: string
    runId: string | null
    buildId: string | null
    artifactId: string | null
    status: string
    stage: string
    message: string | null
    error: string | null
    screenshotRef: string | null
    screenshot: {
        readonly contentType: 'image/png'
        readonly dataBase64: string
    } | null
    consoleErrors: readonly string[]
    networkFailures: readonly BrowserNetworkFailureEntry[]
    httpErrors: readonly BrowserHttpErrorEntry[]
    runtimeSnapshot: RuntimeDebugSnapshot
    runtimeTrace: readonly RuntimeEventEntry[]
    /** §258: SQLite lane state captured at probe time (lane head, dirty pages, mode, last commit). */
    sqliteLaneState: readonly SqliteLaneState[]
}

export interface CaptureComponentJobRunProbeOptions {
    engineManager: EngineManager
    sourcePath: string
    runId?: string | null
    buildId?: string | null
    artifactId?: string | null
    status: string
    stage: string
    message?: string | null
    error?: string | null
    screenshotRef?: string | null
    screenshot?: {
        contentType: 'image/png'
        dataBase64: string
    } | null
    localServerMode?: boolean
    marker?: ComponentJobRunProbeCaptureMarker | null
}

export interface PersistComponentJobRunProbeWriter {
    writeText: (path: string, content: string) => void
}

function readPersistedConsoleErrors(): string[] {
    if (typeof sessionStorage === 'undefined') {
        return runtimeConsoleErrors
    }

    try {
        const raw = sessionStorage.getItem(COMPONENT_JOB_PROBE_CONSOLE_STORAGE_KEY)
        if (!raw) {
            return runtimeConsoleErrors
        }

        const parsed = JSON.parse(raw)
        if (!Array.isArray(parsed)) {
            return runtimeConsoleErrors
        }

        const normalized = parsed
            .filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0)
            .slice(-MAX_COMPONENT_JOB_PROBE_CONSOLE_ERRORS)

        runtimeConsoleErrors = normalized
        return normalized
    } catch {
        return runtimeConsoleErrors
    }
}

function persistConsoleErrors(entries: readonly string[]): void {
    runtimeConsoleErrors = [...entries]

    if (typeof sessionStorage === 'undefined') {
        return
    }

    try {
        if (entries.length === 0) {
            sessionStorage.removeItem(COMPONENT_JOB_PROBE_CONSOLE_STORAGE_KEY)
            return
        }

        sessionStorage.setItem(COMPONENT_JOB_PROBE_CONSOLE_STORAGE_KEY, JSON.stringify(entries))
    } catch {
        // Best-effort persistence only.
    }
}

function readPersistedNetworkFailures(): BrowserNetworkFailureEntry[] {
    if (typeof sessionStorage === 'undefined') {
        return runtimeNetworkFailures
    }

    try {
        const raw = sessionStorage.getItem(COMPONENT_JOB_PROBE_NETWORK_STORAGE_KEY)
        if (!raw) {
            return runtimeNetworkFailures
        }

        const parsed = JSON.parse(raw)
        if (!Array.isArray(parsed)) {
            return runtimeNetworkFailures
        }

        const normalized = parsed
            .filter((entry): entry is BrowserNetworkFailureEntry => (
                typeof entry === 'object'
                && entry !== null
                && typeof (entry as BrowserNetworkFailureEntry).url === 'string'
                && typeof (entry as BrowserNetworkFailureEntry).errorText === 'string'
            ))
            .map(entry => ({
                url: entry.url,
                errorText: entry.errorText,
                ...(entry.method ? { method: entry.method } : {}),
                ...(entry.resourceType ? { resourceType: entry.resourceType } : {}),
            }))
            .slice(-MAX_COMPONENT_JOB_PROBE_NETWORK_FAILURES)

        runtimeNetworkFailures = normalized
        return normalized
    } catch {
        return runtimeNetworkFailures
    }
}

function persistNetworkFailures(entries: readonly BrowserNetworkFailureEntry[]): void {
    runtimeNetworkFailures = entries.map(entry => ({ ...entry }))

    if (typeof sessionStorage === 'undefined') {
        return
    }

    try {
        if (entries.length === 0) {
            sessionStorage.removeItem(COMPONENT_JOB_PROBE_NETWORK_STORAGE_KEY)
            return
        }

        sessionStorage.setItem(COMPONENT_JOB_PROBE_NETWORK_STORAGE_KEY, JSON.stringify(entries))
    } catch {
        // Best-effort persistence only.
    }
}

function stringifyConsoleArg(value: unknown): string {
    if (value instanceof Error) {
        return value.stack || value.message || String(value)
    }

    if (typeof value === 'string') {
        return value
    }

    try {
        return JSON.stringify(value)
    } catch {
        return String(value)
    }
}

function recordConsoleError(message: string): void {
    const normalized = message.trim()
    if (normalized.length === 0) {
        return
    }

    const entries = [...readPersistedConsoleErrors(), normalized].slice(-MAX_COMPONENT_JOB_PROBE_CONSOLE_ERRORS)
    persistConsoleErrors(entries)
}

function installConsoleCapture(): void {
    if (consoleCaptureInstalled || typeof window === 'undefined') {
        return
    }

    window.addEventListener('error', event => {
        recordConsoleError(formatConsoleError({
            source: 'window-error',
            text: event.message || 'Unhandled window error',
            ...(event.filename ? { url: event.filename } : {}),
            ...(typeof event.lineno === 'number' && event.lineno > 0 ? { line: event.lineno } : {}),
            ...(typeof event.colno === 'number' && event.colno > 0 ? { column: event.colno } : {}),
        }))
    })

    window.addEventListener('unhandledrejection', event => {
        const reason = stringifyConsoleArg(event.reason)
        recordConsoleError(`[unhandledrejection] ${reason}`)
    })

    originalConsoleError ??= console.error.bind(console)
    console.error = (...args: unknown[]) => {
        recordConsoleError(args.map(stringifyConsoleArg).join(' '))
        originalConsoleError?.(...args)
    }

    consoleCaptureInstalled = true
}

export function ensureComponentJobRunProbeCaptureInstalled(): void {
    installConsoleCapture()
    readPersistedConsoleErrors()
    readPersistedNetworkFailures()
}

export function clearComponentJobRunProbeSignals(): void {
    persistConsoleErrors([])
    persistNetworkFailures([])
}

export function recordComponentJobRunProbeNetworkFailure(entry: BrowserNetworkFailureEntry): void {
    ensureComponentJobRunProbeCaptureInstalled()

    const entries = [...readPersistedNetworkFailures(), {
        url: entry.url,
        errorText: entry.errorText,
        ...(entry.method ? { method: entry.method } : {}),
        ...(entry.resourceType ? { resourceType: entry.resourceType } : {}),
    }].slice(-MAX_COMPONENT_JOB_PROBE_NETWORK_FAILURES)

    persistNetworkFailures(entries)
}

export function createComponentJobRunProbeCaptureMarker(): ComponentJobRunProbeCaptureMarker {
    ensureComponentJobRunProbeCaptureInstalled()

    const latestTraceEntries = getRuntimeEventTrace({ limit: 1 })
    const latestTraceEvent = latestTraceEntries.length > 0 ? latestTraceEntries[latestTraceEntries.length - 1] : null
    return {
        consoleErrorCount: readPersistedConsoleErrors().length,
        networkFailureCount: readPersistedNetworkFailures().length,
        httpErrorCount: getRecentBrowserHttpErrors().length,
        runtimeTraceSinceId: latestTraceEvent?.id ?? null,
    }
}

function createProbeSyncStates(engineManager: EngineManager): Map<string, SyncState> {
    const syncStates = new Map<string, SyncState>()
    const activeWorkspace = engineManager.getActiveWorkspace()
    if (activeWorkspace?.blobId && activeWorkspace.state) {
        syncStates.set(activeWorkspace.blobId, activeWorkspace.state)
    }

    for (const workspace of engineManager.getSuspendedWorkspaces()) {
        if (!syncStates.has(workspace.blobId) && workspace.state) {
            syncStates.set(workspace.blobId, workspace.state)
        }
    }

    return syncStates
}

function sliceSinceCount<T>(entries: readonly T[], count: number): T[] {
    if (!Number.isFinite(count) || count <= 0) {
        return [...entries]
    }
    if (count >= entries.length) {
        return []
    }
    return entries.slice(count)
}

function normalizeScreenshotRef(value: unknown): string | null {
    if (typeof value !== 'string') {
        return null
    }

    const trimmed = value.trim()
    return trimmed.length > 0 ? trimmed : null
}

function normalizeScreenshot(value: unknown): ComponentJobRunProbe['screenshot'] {
    // Explicit null opt-out — pass screenshot: null to suppress auto-capture
    if (value === null) {
        return null
    }
    if (!value || typeof value !== 'object') {
        return null
    }

    const candidate = value as {
        contentType?: unknown
        dataBase64?: unknown
    }

    if (candidate.contentType !== 'image/png' || typeof candidate.dataBase64 !== 'string') {
        return null
    }

    const trimmedData = candidate.dataBase64.trim()
    return trimmedData.length > 0 ? { contentType: 'image/png', dataBase64: trimmedData } : null
}

/**
 * §247: Capture a screenshot of the current webpage as a serializable PNG payload.
 *
 * Uses html2canvas to render the visible page to a PNG data URL, then encodes
 * it as a base64 string for inclusion in the probe bundle. Falls back to null
 * when html2canvas is unavailable, the page is not ready, or capture fails.
 *
 * Auto-capture is the default for failed runs. Callers can suppress by passing
 * `screenshot: null` explicitly, which normalizeScreenshot handles as opt-out.
 */
async function captureWebpageScreenshot(): Promise<{ contentType: 'image/png'; dataBase64: string } | null> {
    try {
        // html2canvas is loaded from the CDN script tag in the webapp HTML head.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const html2canvas = (window as unknown as { html2canvas?: (el: HTMLElement, opts: unknown) => Promise<HTMLElement> }).html2canvas
        if (typeof html2canvas !== 'function') {
            return null
        }

        const body = document.body
        if (!body) {
            return null
        }

        // Wait for a stable rendering frame before capture
        await new Promise<void>(resolve => {
            requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
        })

        const canvas = await html2canvas(body, {
            useCORS: true,
            allowTaint: true,
            // Scale down to a reasonable max: 1280px wide, auto height.
            // Full-page screenshots for failed-run evidence need to be usable
            // at human-debugging scale but not blow up bundle size.
            width: Math.min(body.scrollWidth, 1280),
            height: Math.min(body.scrollHeight, 1280),
            scale: 1,
            logging: false,
            // Ignore background color so transparency doesn't produce black screenshots
            backgroundColor: '#ffffff',
        } as unknown as Parameters<typeof html2canvas>[1]) as unknown as HTMLCanvasElement

        const dataUrl = canvas.toDataURL('image/png', 0.85)
        if (!dataUrl) {
            return null
        }

        // Strip the "data:image/png;base64," prefix to get the raw base64 string
        const base64 = dataUrl.replace(/^data:image\/png;base64,/, '')
        return {
            contentType: 'image/png',
            dataBase64: base64,
        }
    } catch {
        return null
    }
}

/**
 * §258: Discover active sqlite3mt lanes from runtime trace entries and fetch their
 * current lane-head state from the §256 Part 2 diagnostic endpoint.
 *
 * Active lanes are discovered from sqlite-write-lane trace events (which carry the
 * lane name). Each unique lane is then queried via:
 *   GET /api/cryptoblobstore/workspaces/{workspaceRef}/sqlite/lanes/{lane}/snapshot
 *
 * The workspaceRef is derived from the active workspace blobId. The lane name is
 * base64url-encoded in the endpoint path. The endpoint requires bearer token auth
 * and X-Workspace-Secret header — both obtained via the engine manager's active
 * workspace session.
 *
 * §260: Populates SqliteLaneState for each active lane, including dirtyPageCount,
 * transactionMode, and lastCommitAt from LaneSnapshotResponse (§256 Part 2 extension).
 * If the backend does not yet expose these fields, falls back to safe defaults.
 */
async function captureSqliteLaneState(
    engineManager: EngineManager,
    runtimeTrace: readonly RuntimeEventEntry[],
): Promise<readonly SqliteLaneState[]> {
    // Discover unique lanes from sqlite-write-lane trace events.
    const laneNames = new Set<string>()
    for (const entry of runtimeTrace) {
        if (entry.kind === 'sqlite-write-lane') {
            const data = entry.data as Record<string, unknown>
            const lane = typeof data.lane === 'string' ? data.lane : null
            if (lane) {
                laneNames.add(lane)
            }
        }
    }

    if (laneNames.size === 0) {
        return []
    }

    const activeWorkspace = engineManager.getActiveWorkspace()
    if (!activeWorkspace?.blobId) {
        return []
    }

    const workspaceRef = activeWorkspace.blobId
    const results: SqliteLaneState[] = []

    for (const lane of laneNames) {
        // §256 Part 2: lane snapshot endpoint. The lane name from sqlite-write-lane trace
        // events is already base64url-encoded (it comes from BuildSqlite3MtLane on the
        // backend, which is Base64UrlEncode(UTF8(path))). Use it directly — do not re-encode.
        const snapshotUrl = `/api/cryptoblobstore/workspaces/${encodeURIComponent(workspaceRef)}/sqlite/lanes/${lane}/snapshot`

        try {
            // The fetch call uses the browser's ambient auth (credentials: 'include').
            // Workspace bearer token and X-Workspace-Secret are managed by the SDK's
            // auth layer and are automatically included via the webapp's api client.
            const response = await fetch(snapshotUrl, {
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
            })

            if (!response.ok) {
                // Non-200 responses are expected when the lane is not yet persisted
                // or the workspace SDK does not have write credentials.
                continue
            }

            const snapshot = await response.json() as {
                lane?: string
                pages?: unknown[]
                pageSize?: number
                etag?: string
                source?: string
                dirtyPageCount?: number
                transactionMode?: string
                lastCommitAt?: string | null
            }

// §260: dirtyPageCount, transactionMode, lastCommitAt are now populated from
            // LaneSnapshotResponse (previously defaulted to 0 / 'unknown' / null).
            results.push({
                lane,
                pageCount: Array.isArray(snapshot.pages) ? snapshot.pages.length : 0,
                dirtyPageCount: snapshot.dirtyPageCount ?? 0,
                transactionMode: snapshot.transactionMode ?? 'unknown',
                lastCommitAt: snapshot.lastCommitAt ?? null,
            })
        } catch {
            // Network or parse errors: skip this lane — probe capture must not fail
            // due to lane state unavailability.
        }
    }

    return results
}

export async function captureComponentJobRunProbe(
    options: CaptureComponentJobRunProbeOptions,
): Promise<ComponentJobRunProbe> {
    ensureComponentJobRunProbeCaptureInstalled()

    const { engineManager, marker } = options
    const localServerMode = options.localServerMode ?? false

    const runtimeSnapshot = await buildRuntimeDebugSnapshot(
        engineManager,
        createProbeSyncStates(engineManager),
        { localServerMode },
    )

    const consoleErrors = sliceSinceCount(readPersistedConsoleErrors(), marker?.consoleErrorCount ?? 0)
    const networkFailures = sliceSinceCount(readPersistedNetworkFailures(), marker?.networkFailureCount ?? 0)
    const httpErrors = sliceSinceCount(getRecentBrowserHttpErrors(), marker?.httpErrorCount ?? 0)
    const runtimeTrace = marker?.runtimeTraceSinceId != null
        ? getRuntimeEventTrace({ since: marker.runtimeTraceSinceId })
        : getRuntimeEventTrace()

    // §247: Auto-capture screenshot when the caller has not explicitly opted out.
    // Pass `screenshot: null` or `screenshotRef: null` to suppress auto-capture.
    const autoScreenshot = options.screenshot === undefined
        ? await captureWebpageScreenshot()
        : null

    // §258: Discover active sqlite3mt lanes from the runtime trace and fetch their
    // lane-head state from the §256 Part 2 diagnostic endpoint.
    const sqliteLaneState = await captureSqliteLaneState(engineManager, runtimeTrace)

    return {
        schema: 'secureblob.component-job-run-probe/v1',
        capturedAt: new Date().toISOString(),
        sourcePath: options.sourcePath,
        runId: options.runId ?? null,
        buildId: options.buildId ?? null,
        artifactId: options.artifactId ?? null,
        status: options.status,
        stage: options.stage,
        message: options.message ?? null,
        error: options.error ?? null,
        screenshotRef: normalizeScreenshotRef(options.screenshotRef),
        screenshot: autoScreenshot ?? normalizeScreenshot(options.screenshot),
        consoleErrors,
        networkFailures,
        httpErrors,
        runtimeSnapshot,
        runtimeTrace,
        sqliteLaneState,
    }
}

export function getComponentJobRunProbePaths(sourcePath: string, runId: string): {
    probeDir: string
    probeJson: string
} {
    const runPaths = getComponentRunPaths(sourcePath, runId)
    const probeDir = `${runPaths.outputsDir}/probe`
    return {
        probeDir,
        probeJson: `${probeDir}/probe.json`,
    }
}

export function getComponentJobBuildProbePaths(sourcePath: string, buildId: string): {
    probeDir: string
    probeJson: string
} {
    const buildPaths = getComponentBuildAttemptPaths(sourcePath, buildId)
    const probeDir = `${buildPaths.outputsDir}/probe`
    return {
        probeDir,
        probeJson: `${probeDir}/probe.json`,
    }
}

export function persistComponentJobRunProbe(
    writer: PersistComponentJobRunProbeWriter,
    sourcePath: string,
    runId: string,
    probe: ComponentJobRunProbe,
): { probeDir: string; probeJson: string } {
    const paths = getComponentJobRunProbePaths(sourcePath, runId)
    writer.writeText(paths.probeJson, `${JSON.stringify(probe, null, 2)}\n`)
    return paths
}

export function persistComponentJobBuildProbe(
    writer: PersistComponentJobRunProbeWriter,
    sourcePath: string,
    buildId: string,
    probe: ComponentJobRunProbe,
): { probeDir: string; probeJson: string } {
    const paths = getComponentJobBuildProbePaths(sourcePath, buildId)
    writer.writeText(paths.probeJson, `${JSON.stringify(probe, null, 2)}\n`)
    return paths
}

/** §263: Filename for the incremental live runtime-trace snapshot. */
export const LIVE_RUNTIME_TRACE_FILENAME = 'live-runtime-trace.json'

/**
 * §263: Persist the current in-memory runtime event trace to a live-trace file
 * in the probe directory so the LiveRuntimeTrace tab can poll it during execution.
 *
 * Unlike `persistComponentJobRunProbe` which writes the full probe bundle, this
 * writes only the runtime events array — a lightweight update on every poll cycle.
 *
 * The file is created / overwritten on every call so the UI always sees the latest
 * snapshot. When the run ends, the full probe bundle is written normally and
 * supersedes this file.
 */
export function persistIncrementalRuntimeTrace(
    writer: PersistComponentJobRunProbeWriter,
    sourcePath: string,
    runId: string,
): { liveTraceDir: string; liveTracePath: string } {
    const paths = getComponentJobRunProbePaths(sourcePath, runId)
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { getRuntimeEventTrace } = require('../../testing/runtimeEventTrace') as {
        getRuntimeEventTrace: typeof import('../../testing/runtimeEventTrace').getRuntimeEventTrace
    }
    const events = getRuntimeEventTrace()
    const payload = {
        schema: 'secureblob.component-job-live-runtime-trace/v1',
        flushedAt: new Date().toISOString(),
        runId,
        events,
    }
    writer.writeText(`${paths.probeDir}/${LIVE_RUNTIME_TRACE_FILENAME}`, `${JSON.stringify(payload)}\n`)
    return { liveTraceDir: paths.probeDir, liveTracePath: `${paths.probeDir}/${LIVE_RUNTIME_TRACE_FILENAME}` }
}
